<template>
  <div data-testid="financial-clearance-rules" class="mt-2 rounded-lg border border-surface-200 bg-surface-50 p-3 space-y-3">
    <div>
      <p class="text-sm font-medium text-surface-700">Financial clearance rules</p>
      <p class="text-xs text-surface-500">
        Decide how much of which bills a student must have settled before they can submit course registration.
      </p>
    </div>

    <div class="space-y-1">
      <p class="text-xs font-medium text-surface-600">How scholarships are counted</p>
      <label class="flex items-start gap-2 cursor-pointer">
        <RadioButton v-model="basis" value="net" inputId="clearance-basis-net" data-testid="basis-net" />
        <span>
          <span class="text-sm font-medium text-surface-700">Only payments count</span>
          <span class="block text-xs text-surface-500">A scholarship reduces what the student owes — they must pay the required share of the rest themselves.</span>
        </span>
      </label>
      <label class="flex items-start gap-2 cursor-pointer">
        <RadioButton v-model="basis" value="gross" inputId="clearance-basis-gross" data-testid="basis-gross" />
        <span>
          <span class="text-sm font-medium text-surface-700">Scholarships count as payment</span>
          <span class="block text-xs text-surface-500">An approved scholarship counts toward the required share, as if that money had already been paid.</span>
        </span>
      </label>
    </div>

    <div class="space-y-2">
      <p class="text-xs font-medium text-surface-600">Which bills must be settled</p>
      <p class="text-xs text-surface-500">
        With no rules here, students must fully settle every outstanding bill.
        Add a rule to require only a share of a specific bill instead — the rules then become the whole policy:
        bills without a rule are ignored, and students who have none of these bills register without clearance.
      </p>
      <div
        v-for="(row, index) in rows"
        :key="index"
        class="flex items-center gap-2"
        :data-testid="`peg-row-${index}`"
      >
        <Select
          v-model="row.bill_id"
          :options="optionsFor(row)"
          optionLabel="label"
          optionValue="value"
          filter
          :loading="billsLoading"
          placeholder="Choose a bill"
          class="flex-1"
          :data-testid="`peg-bill-${index}`"
        />
        <InputNumber
          v-model="row.percentage"
          :min="1"
          :max="100"
          suffix="%"
          :inputStyle="{ width: '5.5rem' }"
          :data-testid="`peg-percentage-${index}`"
        />
        <Button
          icon="pi pi-trash"
          text
          severity="danger"
          aria-label="Remove rule"
          :data-testid="`peg-remove-${index}`"
          @click="removeRow(index)"
        />
      </div>
      <Button
        label="Add bill rule"
        icon="pi pi-plus"
        text
        size="small"
        data-testid="btn-add-peg"
        @click="addRow"
      />
    </div>

    <Message v-if="deadRuleWarning" severity="warn" size="small" data-testid="dead-rules-warning">
      {{ deadRuleWarning }}
    </Message>

    <Message v-if="validationError" severity="warn" size="small" data-testid="pegs-validation-error">
      {{ validationError }}
    </Message>

    <div class="flex justify-end">
      <Button
        label="Save clearance rules"
        size="small"
        :disabled="!isDirty || !!validationError"
        :loading="saving"
        data-testid="btn-save-clearance-rules"
        @click="save"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { useToast } from 'primevue/usetoast';
import { formatCurrency } from '@/utils/currency';
import { billNumberLabel } from '@/utils/billNumberLabel';
import { useBills, type Bill } from '@/composables/billing/useBills';
import { useSchoolQuery } from '@/composables/queries/useSchoolQuery';
import { useUpdateSchoolSettingsMutation } from '@/types/vue-apollo-helpers';

/**
 * Editor for course_reg_clearance in the school settings JSON: the
 * scholarship basis ('net' | 'gross') and the bill pegs
 * ([{ bill_id, percentage }]). The pegs list REPLACES the stored rule
 * wholesale on save — removing a row here removes it for good, which is
 * why saving goes through the list-replace merge on the backend.
 */

interface PegRow {
  bill_id: string | null;
  percentage: number | null;
}

interface ClearanceConfig {
  basis?: string;
  bill_pegs?: { bill_id: number | string; percentage: number }[];
}

const toast = useToast();
const { school, refetchSchool } = useSchoolQuery();
const { mutate: updateSettingsMutation } = useUpdateSchoolSettingsMutation();

const { bills, filters, loading: billsLoading, load } = useBills();
// Anything a student could be asked to settle is peggable — only drafts and
// voided bills are out (the gate drops pegs on those instead of opening).
filters.status = ['published', 'partially_paid', 'paid', 'overdue'];
load();

const basis = ref<'net' | 'gross'>('net');
const rows = ref<PegRow[]>([]);
const saving = ref(false);

const savedConfig = computed<ClearanceConfig>(() => {
  const settings = (school.value as { settings?: { course_reg_clearance?: ClearanceConfig } } | null)?.settings;
  return settings?.course_reg_clearance ?? {};
});

const snapshot = (b: string, r: PegRow[]) =>
  JSON.stringify({ basis: b, pegs: r.map((row) => ({ bill_id: row.bill_id, percentage: row.percentage })) });

const savedSnapshot = ref(snapshot('net', []));

watch(
  savedConfig,
  (config) => {
    basis.value = config.basis === 'gross' ? 'gross' : 'net';
    rows.value = (config.bill_pegs ?? []).map((peg) => ({
      bill_id: String(peg.bill_id),
      percentage: Number(peg.percentage),
    }));
    savedSnapshot.value = snapshot(basis.value, rows.value);
  },
  { immediate: true, deep: true },
);

const isDirty = computed(() => snapshot(basis.value, rows.value) !== savedSnapshot.value);

const validationError = computed(() => {
  if (rows.value.some((row) => !row.bill_id)) {
    return 'Every rule needs a bill — pick one or remove the empty row.';
  }
  const ids = rows.value.map((row) => row.bill_id);
  if (new Set(ids).size !== ids.length) {
    return 'Each bill can only appear in one rule.';
  }
  if (rows.value.some((row) => !row.percentage || row.percentage < 1 || row.percentage > 100)) {
    return 'The required share must be between 1% and 100%.';
  }
  return null;
});

const billOptions = computed(() =>
  (bills.value as Bill[]).map((bill) => ({ value: String(bill.id), label: billOptionLabel(bill) })),
);

// A saved rule can point at a bill that has since been voided or deleted —
// the picker no longer offers it. Surface it instead of showing a blank
// select: the row keeps a synthetic option, and a warning explains that dead
// rules do nothing (and that an all-dead config falls back to full
// settlement on the backend guard).
const deadRuleIds = computed(() => {
  if (billsLoading.value) return [];
  const live = new Set(billOptions.value.map((option) => option.value));
  return rows.value
    .map((row) => row.bill_id)
    .filter((id): id is string => !!id && !live.has(id));
});

const deadRuleWarning = computed(() => {
  if (deadRuleIds.value.length === 0) return null;
  const liveRuleExists = rows.value.some((row) => row.bill_id && !deadRuleIds.value.includes(row.bill_id));
  return liveRuleExists
    ? 'Some rules point at bills that no longer exist or were voided — those rules have no effect. Remove them or pick a current bill.'
    : 'None of these rules point at an existing bill anymore. Until you fix or remove them, students must fully settle every outstanding bill.';
});

function optionsFor(row: PegRow) {
  if (!row.bill_id || !deadRuleIds.value.includes(row.bill_id)) return billOptions.value;
  return [
    { value: row.bill_id, label: 'Removed or voided bill' },
    ...billOptions.value,
  ];
}

function billOptionLabel(bill: Bill): string {
  const parts = [billableName(bill)];
  const period = [bill.session?.name, bill.academicCalendar?.termStructure?.name].filter(Boolean).join(' ');
  if (period) parts.push(period);
  parts.push(formatCurrency(Number(bill.amount) || 0));
  return parts.join(' — ');
}

function billableName(bill: Bill): string {
  const billable = bill.billable;
  if (!billable) return billNumberLabel(bill);
  if ('first_name' in billable) return `${billable.first_name} ${billable.surname}`;
  if ('classroom' in billable) {
    return `${billable.classroom?.name ?? ''}${billable.section?.name ? ` - ${billable.section.name}` : ''}` || billNumberLabel(bill);
  }
  if ('program_name' in billable) return billable.program_name ?? billNumberLabel(bill);
  return 'name' in billable && billable.name ? String(billable.name) : billNumberLabel(bill);
}

function addRow() {
  rows.value.push({ bill_id: null, percentage: 100 });
}

function removeRow(index: number) {
  rows.value.splice(index, 1);
}

async function save() {
  saving.value = true;
  try {
    await updateSettingsMutation({
      settings: {
        course_reg_clearance: {
          basis: basis.value,
          bill_pegs: rows.value.map((row) => ({
            bill_id: Number(row.bill_id),
            percentage: Number(row.percentage),
          })),
        },
      },
    });
    savedSnapshot.value = snapshot(basis.value, rows.value);
    toast.add({ severity: 'success', summary: 'Saved', detail: 'Financial clearance rules updated.', life: 3000 });
    await refetchSchool();
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : 'Failed to save the clearance rules.';
    toast.add({ severity: 'error', summary: 'Error', detail: message, life: 3000 });
  } finally {
    saving.value = false;
  }
}
</script>
